import React, { useEffect, useState } from 'react';
import { useTranslate } from '../../Hook/useTranslate';
import useDynamicTranslation from '../../Hook/useDynamicTranslation';
import './ReasonDialog.css';

/**
 * Modal that asks for a reason: preset options (e.g. admin-managed cancellation reasons)
 * plus "Other" with free text. Calls onSubmit(reason) and stays open while it runs.
 */
export default function ReasonDialog({ open, title, reasons = [], submitLabel, onSubmit, onClose }) {
  const t = useTranslate();
  const { getText } = useDynamicTranslation(reasons);
  const [selected, setSelected] = useState('');
  const [otherText, setOtherText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(reasons.length ? '' : '__other');
      setOtherText('');
      setSubmitting(false);
    }
  }, [open, reasons.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const reason = selected === '__other' ? otherText.trim() : selected;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(reason);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reason-dialog__backdrop" onClick={() => !submitting && onClose()}>
      <form
        className="reason-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reason-dialog-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2 id="reason-dialog-title" className="reason-dialog__title">{title}</h2>

        <div className="reason-dialog__options" role="radiogroup">
          {reasons.map((r) => (
            <label key={r} className={`reason-dialog__option ${selected === r ? 'is-selected' : ''}`}>
              <input type="radio" name="reason" value={r} checked={selected === r} onChange={() => setSelected(r)} />
              <span>{getText(r)}</span>
            </label>
          ))}
          <label className={`reason-dialog__option ${selected === '__other' ? 'is-selected' : ''}`}>
            <input type="radio" name="reason" value="__other" checked={selected === '__other'} onChange={() => setSelected('__other')} />
            <span>{t('Other')}</span>
          </label>
        </div>

        {selected === '__other' && (
          <textarea
            className="reason-dialog__text"
            rows={3}
            maxLength={500}
            autoFocus
            placeholder={t('Please describe the reason')}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
          />
        )}

        <div className="reason-dialog__actions">
          <button type="button" className="reason-dialog__btn reason-dialog__btn--ghost" onClick={onClose} disabled={submitting}>
            {t('Cancel')}
          </button>
          <button type="submit" className="reason-dialog__btn reason-dialog__btn--primary" disabled={!reason || submitting}>
            {submitting ? t('Submitting...') : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
